import {
  CTCSS_TONES,
  DTCS_CODES,
  compareImages,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BLOCK_SIZE = 16
const BLOCK_COUNT = 0x215
const MEMORY_SIZE = BLOCK_SIZE * BLOCK_COUNT
const CHANNEL_OFFSET = 0x10
const CHANNEL_COUNT = 200
const CHANNEL_SIZE = 16
const ENABLE_OFFSET = 0x0e50
const SCAN_OFFSET = 0x0e70
const NAME_OFFSET = 0x1000
const NAME_SIZE = 8
const TX_FREQUENCY_OFFSET = 0x1700
const TX_FREQUENCY_SIZE = 4

type AutoShiftRange = readonly [
  minimum: number,
  maximum: number,
  duplex: 0 | 2,
]

type SC35ProfileSpec = {
  id: string
  model: string
  identity: string
  nameLength: 6 | 8
  offsetMultiplier: 1 | 2
  powerLevels: readonly ['High', 'Mid', 'Low']
  legalSteps: readonly number[]
  autoShift: readonly AutoShiftRange[]
  bands: readonly (readonly [number, number])[]
  offWhenShiftLeavesBand?: boolean
}

export type YaesuSC35Profile = {
  protocol: 'sc35'
  id: string
  vendor: 'Yaesu'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: 200
  nameLength: 6 | 8
  powerLevels: readonly ['High', 'Mid', 'Low']
  memorySize: number
  identity: readonly number[]
  offsetMultiplier: 1 | 2
  legalSteps: readonly number[]
  autoShift: readonly AutoShiftRange[]
  bands: readonly (readonly [number, number])[]
  offWhenShiftLeavesBand: boolean
}

const US_AUTO_SHIFT = [
  [145_100_000, 145_495_000, 2],
  [146_000_000, 146_395_000, 0],
  [146_600_000, 146_995_000, 2],
  [147_000_000, 147_395_000, 0],
  [147_600_000, 147_995_000, 2],
] as const
const EU_AUTO_SHIFT = [
  [145_600_000, 145_800_000, 2],
  [438_200_000, 439_425_000, 2],
] as const
const DUAL_BAND = [
  [65_000_000, 108_000_000],
  [136_000_000, 174_000_000],
  [400_000_000, 480_000_000],
] as const
const VHF_BAND = [
  [65_000_000, 108_000_000],
  [136_000_000, 174_000_000],
] as const
const US_STEPS = [5, 10, 12.5, 15, 20, 25, 50, 100] as const
const ALL_STEPS = [5, 6.25, 10, 12.5, 15, 20, 25, 50, 100] as const

function ascii(value: string): Uint8Array {
  return new TextEncoder().encode(value)
}

function profile(
  spec: SC35ProfileSpec,
): YaesuSC35Profile {
  return {
    protocol: 'sc35',
    id: spec.id,
    vendor: 'Yaesu',
    model: spec.model,
    summary:
      'Yaesu SCU-35 cable family with 200 memories, channel names, tones, and repeater shifts.',
    tags: [
      'Experimental',
      'SCU-35',
      'Read + write',
      '200 channels',
      `${spec.nameLength}-character names`,
    ],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: 200,
    nameLength: spec.nameLength,
    powerLevels: spec.powerLevels,
    memorySize: MEMORY_SIZE,
    identity: Array.from(ascii(spec.identity)),
    offsetMultiplier: spec.offsetMultiplier,
    legalSteps: spec.legalSteps,
    autoShift: spec.autoShift,
    bands: spec.bands,
    offWhenShiftLeavesBand: spec.offWhenShiftLeavesBand ?? false,
  }
}

export const YAESU_SC35_PROFILES: readonly YaesuSC35Profile[] = [
  profile({
    id: 'yaesu-ft-4xr',
    model: 'FT-4XR',
    identity: 'IFT-35R\u0000\u0000V100\u0000\u0000',
    nameLength: 6,
    offsetMultiplier: 1,
    powerLevels: ['High', 'Mid', 'Low'],
    legalSteps: US_STEPS,
    autoShift: US_AUTO_SHIFT,
    bands: DUAL_BAND,
  }),
  profile({
    id: 'yaesu-ft-4xe',
    model: 'FT-4XE',
    identity: 'IFT-35R\u0000\u0000V100\u0000\u0000',
    nameLength: 6,
    offsetMultiplier: 1,
    powerLevels: ['High', 'Mid', 'Low'],
    legalSteps: ALL_STEPS,
    autoShift: EU_AUTO_SHIFT,
    bands: DUAL_BAND,
  }),
  profile({
    id: 'yaesu-ft-4vr',
    model: 'FT-4VR',
    identity: 'IFT-15R\u0000\u0000V100\u0000\u0000',
    nameLength: 6,
    offsetMultiplier: 1,
    powerLevels: ['High', 'Mid', 'Low'],
    legalSteps: US_STEPS,
    autoShift: US_AUTO_SHIFT,
    bands: VHF_BAND,
  }),
  profile({
    id: 'yaesu-ft-65r',
    model: 'FT-65R',
    identity: 'IH-420\u0000\u0000\u0000V100\u0000\u0000',
    nameLength: 8,
    offsetMultiplier: 2,
    powerLevels: ['High', 'Mid', 'Low'],
    legalSteps: ALL_STEPS,
    autoShift: US_AUTO_SHIFT,
    bands: DUAL_BAND,
    offWhenShiftLeavesBand: true,
  }),
  profile({
    id: 'yaesu-ft-65e',
    model: 'FT-65E',
    identity: 'IH-420\u0000\u0000\u0000V100\u0000\u0000',
    nameLength: 8,
    offsetMultiplier: 2,
    powerLevels: ['High', 'Mid', 'Low'],
    legalSteps: ALL_STEPS,
    autoShift: EU_AUTO_SHIFT,
    bands: DUAL_BAND,
  }),
  profile({
    id: 'yaesu-ft-25r',
    model: 'FT-25R',
    identity: 'IFT-25R\u0000\u0000V100\u0000\u0000',
    nameLength: 8,
    offsetMultiplier: 2,
    powerLevels: ['High', 'Mid', 'Low'],
    legalSteps: US_STEPS,
    autoShift: US_AUTO_SHIFT,
    bands: VHF_BAND,
  }),
]

function emptyChannel(
  number: number,
  profile: YaesuSC35Profile,
): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: profile.powerLevels[0],
    skip: true,
  }
}

export function decodeSC35Frequency(raw: Uint8Array): number | null {
  if (raw.byteLength !== 4 || raw.every((value) => value === 0xff)) return null
  let value = 0
  for (const byte of raw) {
    const high = byte >> 4
    const low = byte & 0x0f
    if (high > 9 || low > 9) return null
    value = value * 100 + high * 10 + low
  }
  return value * 10
}

export function encodeSC35Frequency(frequency: number): Uint8Array {
  if (!Number.isFinite(frequency) || frequency <= 0 || frequency % 10 !== 0) {
    throw new Error('The SCU-35 frequency must be a positive 10 Hz increment.')
  }
  let value = Math.round(frequency / 10)
  const raw = new Uint8Array(4)
  for (let index = raw.byteLength - 1; index >= 0; index -= 1) {
    const part = value % 100
    raw[index] = ((Math.floor(part / 10) << 4) | (part % 10)) & 0xff
    value = Math.floor(value / 100)
  }
  if (value !== 0) {
    throw new Error(
      `${(frequency / 1_000_000).toFixed(6)} MHz cannot be represented by this Yaesu.`,
    )
  }
  return raw
}

function channelEnabled(image: Uint8Array, index: number): boolean {
  return (image[ENABLE_OFFSET + (index >> 3)] & (1 << (index & 7))) !== 0
}

function setChannelEnabled(
  image: Uint8Array,
  index: number,
  enabled: boolean,
): void {
  const offset = ENABLE_OFFSET + (index >> 3)
  const mask = 1 << (index & 7)
  image[offset] = enabled
    ? image[offset] | mask
    : image[offset] & ~mask
}

function channelScanned(image: Uint8Array, index: number): boolean {
  return (image[SCAN_OFFSET + (index >> 3)] & (1 << (index & 7))) !== 0
}

function setChannelScanned(
  image: Uint8Array,
  index: number,
  scanned: boolean,
): void {
  const offset = SCAN_OFFSET + (index >> 3)
  const mask = 1 << (index & 7)
  image[offset] = scanned
    ? image[offset] | mask
    : image[offset] & ~mask
}

function decodeName(
  image: Uint8Array,
  profile: YaesuSC35Profile,
  index: number,
): string {
  let name = ''
  const offset = NAME_OFFSET + index * NAME_SIZE
  for (let character = 0; character < profile.nameLength; character += 1) {
    const code = image[offset + character]
    if (code === 0) break
    name += String.fromCharCode(code === 0x7f ? 0x20 : code)
  }
  return name.trimEnd()
}

function encodeName(
  image: Uint8Array,
  profile: YaesuSC35Profile,
  index: number,
  name: string,
): void {
  const offset = NAME_OFFSET + index * NAME_SIZE
  const normalized = name.slice(0, profile.nameLength).padEnd(NAME_SIZE, ' ')
  image.set(Array.from(normalized, (character) => character.charCodeAt(0) & 0x7f), offset)
}

function automaticDuplex(
  profile: YaesuSC35Profile,
  frequency: number,
): 0 | 2 | null {
  let result: 0 | 2 | null = null
  for (const [minimum, maximum, duplex] of profile.autoShift) {
    if (frequency >= minimum && frequency < maximum) result = duplex
  }
  return result
}

function shiftOffset(raw: Uint8Array, profile: YaesuSC35Profile): number {
  const units = raw[10] | (raw[11] << 8)
  return units * 25_000 * profile.offsetMultiplier
}

function txFrequencyForChannel(
  image: Uint8Array,
  profile: YaesuSC35Profile,
  raw: Uint8Array,
  index: number,
  rxFrequency: number,
  duplex: number,
): number | null {
  const offset = shiftOffset(raw, profile)
  const outsideBand = (frequency: number) =>
    !profile.bands.some(
      ([minimum, maximum]) => frequency >= minimum && frequency <= maximum,
    )
  if (duplex === 0) {
    const txFrequency = rxFrequency + offset
    return profile.offWhenShiftLeavesBand && outsideBand(txFrequency)
      ? null
      : txFrequency
  }
  if (duplex === 2) {
    const txFrequency = rxFrequency - offset
    return profile.offWhenShiftLeavesBand && outsideBand(txFrequency)
      ? null
      : txFrequency
  }
  if (duplex === 4) return null
  if (duplex === 6) {
    return decodeSC35Frequency(
      image.slice(
        TX_FREQUENCY_OFFSET + index * TX_FREQUENCY_SIZE,
        TX_FREQUENCY_OFFSET + (index + 1) * TX_FREQUENCY_SIZE,
      ),
    )
  }
  if (duplex === 5) {
    const automatic = automaticDuplex(profile, rxFrequency)
    if (automatic === 0) return rxFrequency + offset
    if (automatic === 2) return rxFrequency - offset
    return null
  }
  return rxFrequency
}

function decodeTone(
  raw: Uint8Array,
): Pick<WebChannel, 'toneMode' | 'ctcss' | 'dtcs' | 'dtcsPolarity'> {
  const sqlType = raw[14]
  if (sqlType === 5) {
    const code = raw[8] || raw[7]
    return {
      toneMode: code ? 'dtcs' : 'none',
      ctcss: null,
      dtcs: code ? DTCS_CODES[code - 1] ?? DTCS_CODES[0] : null,
      dtcsPolarity: 'N',
    }
  }
  const code =
    sqlType === 2
      ? raw[5] || raw[6]
      : raw[6] || raw[5]
  return {
    toneMode: code && sqlType !== 0 && sqlType !== 6 ? 'ctcss' : 'none',
    ctcss: code ? CTCSS_TONES[code - 1] ?? CTCSS_TONES[0] : null,
    dtcs: null,
    dtcsPolarity: 'N',
  }
}

export function parseSC35Channel(
  image: Uint8Array,
  profile: YaesuSC35Profile,
  number: number,
): WebChannel {
  if (number < profile.channelMinimum || number > profile.channelMaximum) {
    throw new Error(`Channel ${number} is outside this Yaesu's memory range.`)
  }
  const index = number - profile.channelMinimum
  if (!channelEnabled(image, index)) return emptyChannel(number, profile)
  const offset = CHANNEL_OFFSET + index * CHANNEL_SIZE
  const raw = image.slice(offset, offset + CHANNEL_SIZE)
  const rxFrequency = decodeSC35Frequency(raw.slice(1, 5))
  if (raw.byteLength !== CHANNEL_SIZE || !rxFrequency) {
    return emptyChannel(number, profile)
  }
  const duplex = raw[9] & 0x07
  const powerIndex = 2 - (raw[0] & 0x03)
  return {
    number,
    empty: false,
    name: decodeName(image, profile, index),
    rxFrequency,
    txFrequency: txFrequencyForChannel(
      image,
      profile,
      raw,
      index,
      rxFrequency,
      duplex,
    ),
    ...decodeTone(raw),
    mode: (raw[12] & 0x01) !== 0 ? 'NFM' : 'FM',
    power: profile.powerLevels[powerIndex] ?? profile.powerLevels[0],
    skip: !channelScanned(image, index),
  }
}

export function parseSC35Channels(
  image: Uint8Array,
  profile: YaesuSC35Profile,
): WebChannel[] {
  return Array.from(
    { length: profile.channelMaximum },
    (_, index) => parseSC35Channel(image, profile, index + profile.channelMinimum),
  )
}

function requiredSC35Step(
  profile: YaesuSC35Profile,
  frequency: number,
): number {
  for (const step of profile.legalSteps) {
    if (frequency % Math.round(step * 1000) === 0) return step
  }
  return profile.legalSteps[0]
}

function stepCode(profile: YaesuSC35Profile, step: number): number {
  const codes = [0, 5, 6.25, 10, 12.5, 15, 20, 25, 50, 100]
  const index = codes.indexOf(step)
  return index < 0 ? 0 : index
}

function encodeTone(raw: Uint8Array, channel: WebChannel): void {
  raw.fill(0, 5, 9)
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    const index = CTCSS_TONES.indexOf(
      channel.ctcss as (typeof CTCSS_TONES)[number],
    )
    if (index < 0) throw new Error('Unsupported CTCSS tone for this Yaesu.')
    raw[5] = index + 1
    raw[6] = index + 1
    raw[14] = 3
  } else if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const index = DTCS_CODES.indexOf(channel.dtcs as (typeof DTCS_CODES)[number])
    if (index < 0) throw new Error('Unsupported DTCS code for this Yaesu.')
    raw[7] = index + 1
    raw[8] = index + 1
    raw[14] = 5
  } else {
    raw[14] = 0
  }
}

function writeTxFrequency(
  image: Uint8Array,
  index: number,
  frequency: number,
): void {
  const offset = TX_FREQUENCY_OFFSET + index * TX_FREQUENCY_SIZE
  image.set(encodeSC35Frequency(frequency), offset)
}

export function applySC35Channel(
  image: Uint8Array,
  profile: YaesuSC35Profile,
  channel: WebChannel,
): void {
  const index = channel.number - profile.channelMinimum
  if (channel.empty) {
    setChannelEnabled(image, index, false)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')

  const offset = CHANNEL_OFFSET + index * CHANNEL_SIZE
  if (image[offset] === 0xff) {
    image.fill(0, offset, offset + CHANNEL_SIZE)
  }
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  raw[9] &= 0xf8
  raw[12] &= 0xfe
  const powerIndex = profile.powerLevels.indexOf(
    channel.power as YaesuSC35Profile['powerLevels'][number],
  )
  raw[0] = 2 - Math.max(0, powerIndex)
  raw.set(encodeSC35Frequency(channel.rxFrequency), 1)
  encodeTone(raw, channel)

  const txFrequency = channel.txFrequency
  if (txFrequency === null) {
    raw[9] = (raw[9] & 0xf8) | 4
    raw[10] = 0
    raw[11] = 0
    writeTxFrequency(image, index, channel.rxFrequency)
  } else if (txFrequency === channel.rxFrequency) {
    raw[9] = (raw[9] & 0xf8) | 1
    raw[10] = 0
    raw[11] = 0
    writeTxFrequency(image, index, channel.rxFrequency)
  } else {
    const delta = txFrequency - channel.rxFrequency
    const shiftUnits =
      Math.abs(delta) / (25_000 * profile.offsetMultiplier)
    const preserveSplit = (raw[9] & 0x07) === 6
    if (
      !preserveSplit &&
      Number.isInteger(shiftUnits) &&
      shiftUnits >= 1 &&
      shiftUnits <= 0xffff
    ) {
      raw[9] = (raw[9] & 0xf8) | (delta < 0 ? 2 : 0)
      raw[10] = shiftUnits & 0xff
      raw[11] = (shiftUnits >> 8) & 0xff
      writeTxFrequency(image, index, channel.rxFrequency)
    } else {
      raw[9] = (raw[9] & 0xf8) | 6
      raw[10] = 0
      raw[11] = 0
      writeTxFrequency(image, index, txFrequency)
    }
  }

  raw[12] = (raw[12] & 0xfe) | (channel.mode === 'NFM' ? 1 : 0)
  const existingStep = raw[13]
  if (existingStep === 0xff || existingStep > 9) {
    raw[13] = stepCode(
      profile,
      requiredSC35Step(profile, channel.rxFrequency),
    )
  }
  setChannelEnabled(image, index, true)
  setChannelScanned(image, index, !channel.skip)
  encodeName(image, profile, index, channel.name)
}

function checksum(data: Uint8Array): number {
  let result = 0
  for (const value of data) result = (result + value) & 0xff
  return result
}

function concat(...chunks: Uint8Array[]): Uint8Array {
  const result = new Uint8Array(
    chunks.reduce((total, chunk) => total + chunk.byteLength, 0),
  )
  let offset = 0
  for (const chunk of chunks) {
    result.set(chunk, offset)
    offset += chunk.byteLength
  }
  return result
}

async function sendCommand(
  transport: RadioTransport,
  command: Uint8Array,
  responseLength: number | 'until-ack',
  timeoutMs = 3000,
): Promise<Uint8Array> {
  await transport.write(command)
  const echo = await transport.readExact(command.byteLength, timeoutMs)
  if (compareImages(command, echo) !== null) {
    throw new Error('The SCU-35 cable returned a corrupted command echo.')
  }
  if (responseLength === 'until-ack') {
    const chunks: Uint8Array[] = []
    let size = 0
    while (size < 256) {
      const byte = await transport.readExact(1, timeoutMs)
      if (byte[0] === ACK) return concat(...chunks)
      chunks.push(byte)
      size += 1
    }
    throw new Error('The Yaesu radio returned an oversized identity response.')
  }
  const response =
    responseLength > 0
      ? await transport.readExact(responseLength, timeoutMs)
      : new Uint8Array(0)
  if ((await transport.readExact(1, timeoutMs))[0] !== ACK) {
    throw new Error('The Yaesu radio did not acknowledge the command.')
  }
  return response
}

function addressCommand(prefix: number, address: number): Uint8Array {
  return Uint8Array.of(
    prefix,
    (address >> 8) & 0xff,
    address & 0xff,
    BLOCK_SIZE,
  )
}

async function startRadio(
  profile: YaesuSC35Profile,
  transport: RadioTransport,
): Promise<string> {
  await transport.discardInput()
  let response: Uint8Array = new Uint8Array(0)
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      response = await sendCommand(
        transport,
        ascii('PROGRAM'),
        2,
        5000,
      )
      if (new TextDecoder('ascii').decode(response) === 'QX') break
    } catch {
      await transport.discardInput()
    }
  }
  if (new TextDecoder('ascii').decode(response) !== 'QX') {
    throw new Error('The Yaesu radio refused to enter SCU-35 clone mode.')
  }

  const identity = await sendCommand(
    transport,
    Uint8Array.of(0x02),
    'until-ack',
    5000,
  )
  const expectedPrefix = profile.identity.findIndex((value) => value === 0)
  const receivedPrefix = identity.findIndex((value) => value === 0)
  if (
    expectedPrefix >= 0 &&
    !profile.identity
      .slice(0, expectedPrefix)
      .every((value, index) => identity[index] === value)
  ) {
    throw new Error(
      `The connected radio does not identify as the selected ${profile.model}.`,
    )
  }
  if (identity.at(-1) === 3) {
    throw new Error(
      'This regional FT-25R/FT-65R variant uses a different repeater-offset scale and is not supported by the browser driver yet.',
    )
  }
  return (
    new TextDecoder('ascii')
      .decode(identity.slice(0, receivedPrefix >= 0 ? receivedPrefix : identity.length))
      .replace(/[^\x20-\x7e]/g, '')
      .trim() || profile.model
  )
}

async function endRadio(transport: RadioTransport): Promise<void> {
  await sendCommand(transport, ascii('END'), 0)
}

async function readBlock(
  transport: RadioTransport,
  address: number,
): Promise<Uint8Array> {
  const command = addressCommand(0x52, address)
  const response = await sendCommand(transport, command, 21)
  if (
    response[0] !== 0x57 ||
    !command.slice(1).every((value, index) => response[index + 1] === value)
  ) {
    throw new Error(
      `Unexpected Yaesu read reply at 0x${address.toString(16)}.`,
    )
  }
  if (checksum(response.slice(1, 20)) !== response[20]) {
    throw new Error(
      `Bad Yaesu read checksum at 0x${address.toString(16)}.`,
    )
  }
  return response.slice(4, 20)
}

async function writeBlock(
  transport: RadioTransport,
  address: number,
  data: Uint8Array,
): Promise<void> {
  const payload = concat(
    Uint8Array.of((address >> 8) & 0xff, address & 0xff, BLOCK_SIZE),
    data,
  )
  const command = concat(
    Uint8Array.of(0x57),
    payload,
    Uint8Array.of(checksum(payload), ACK),
  )
  await sendCommand(transport, command, 0)
}

export async function readSC35RadioWithTransport(
  profile: YaesuSC35Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  const reportedModel = await startRadio(profile, transport)
  const image = new Uint8Array(profile.memorySize)
  for (let block = 0; block < BLOCK_COUNT; block += 1) {
    const address = block * BLOCK_SIZE
    image.set(await readBlock(transport, address), address)
    onProgress?.({
      current: block + 1,
      total: BLOCK_COUNT,
      label: 'Reading Yaesu SCU-35 memory',
    })
  }
  await endRadio(transport)
  return { image, reportedModel }
}

export async function writeSC35RadioWithTransport(
  profile: YaesuSC35Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < profile.memorySize) {
    throw new Error('The selected image is smaller than this Yaesu memory map.')
  }
  await startRadio(profile, transport)
  for (let block = 1; block < BLOCK_COUNT; block += 1) {
    const address = block * BLOCK_SIZE
    await writeBlock(transport, address, image.slice(address, address + BLOCK_SIZE))
    onProgress?.({
      current: block,
      total: BLOCK_COUNT - 1,
      label: 'Writing Yaesu SCU-35 memory',
    })
  }
  await endRadio(transport)
}
